import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function md(text) {
  if (!text) return null;
  return text.split("\n").map((l, i) => {
    const k = `L${i}-${l.slice(0, 20)}`;
    if (l.startsWith("## ")) return <h3 key={k} className="font-heading text-lg text-[#D4AF37] mt-4 mb-2">{l.slice(3)}</h3>;
    if (l.startsWith("# ")) return <h2 key={k} className="font-heading text-xl text-[#E6E1D6] mt-4 mb-2">{l.slice(2)}</h2>;
    if (!l.trim()) return <div key={k} className="h-2" />;
    return <p key={k} className="text-sm text-[#E6E1D6] leading-relaxed font-light">{l}</p>;
  });
}

export default function NumerologyPanel() {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    if (!name || !dob) { toast.error("Enter name and date of birth"); return; }
    setLoading(true);
    try { setData(await api.numerology(name, dob, true)); }
    catch { toast.error("Numerology failed"); }
    setLoading(false);
  };

  const Card = ({ title, node }) => (
    <div className="border border-[#2A2D35] bg-[#181A1F] p-4" data-testid={`num-${title.toLowerCase().replace(/[^a-z]/g,'-')}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">{title}</div>
      <div className="mt-1 flex items-baseline gap-3">
        <div className="font-heading text-4xl text-[#E6E1D6]">{node.number}</div>
        {node.master && (
          <div className="text-[#D4AF37] font-mono text-xs uppercase tracking-widest">Master</div>
        )}
        {!node.master && node.number !== node.reduced && (
          <div className="text-[#6E6B64] font-mono text-sm">→ {node.reduced}</div>
        )}
      </div>
      <div className="text-xs text-[#A8896C] mt-1">Ruling: {node.ruling_planet}</div>
      {node.master_meaning && (
        <div className="text-[10px] text-[#A19E95] italic mt-1">{node.master_meaning}</div>
      )}
      {node.favourable?.colours && (
        <div className="mt-3 space-y-1 text-xs text-[#A19E95]">
          <div><b className="text-[#E6E1D6]">Colours:</b> {node.favourable.colours.join(", ")}</div>
          <div><b className="text-[#E6E1D6]">Direction:</b> {node.favourable.direction}</div>
          <div><b className="text-[#E6E1D6]">Gemstone:</b> {node.favourable.gemstone}</div>
          <div><b className="text-[#E6E1D6]">Days:</b> {node.favourable.days?.join(", ")}</div>
          <div><b className="text-[#E6E1D6]">Compatible:</b> {node.favourable.compatible?.join(", ")}</div>
        </div>
      )}
      {node.remedy && <div className="mt-2 text-xs text-[#7B906F] italic">{node.remedy}</div>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="numerology-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div>
          <Label className="text-[#A19E95] text-xs uppercase tracking-widest">Full Name</Label>
          <Input data-testid="num-name" value={name} onChange={e => setName(e.target.value)}
                 className="mt-2 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]" />
        </div>
        <div>
          <Label className="text-[#A19E95] text-xs uppercase tracking-widest">Date of Birth</Label>
          <Input data-testid="num-dob" type="date" value={dob} onChange={e => setDob(e.target.value)}
                 className="mt-2 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono" />
        </div>
        <Button data-testid="num-submit" onClick={compute} disabled={loading}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          {loading ? "Distilling…" : "Compute Numerology"}
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Card title="Life Path" node={data.life_path} />
            <Card title="Destiny" node={data.destiny} />
            <Card title="Soul Urge" node={data.soul_urge} />
            <Card title="Personality" node={data.personality} />
            <Card title="Birth Number" node={data.birth_number} />
          </div>
          {data.interpretation && (
            <div className="border border-[#2A2D35] bg-[#181A1F] p-6" data-testid="num-interpretation">
              {md(data.interpretation)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
